/**
 * Performance Monitoring Utilities for Code Splitting
 * Tracks the effectiveness of our chunk loading strategy
 */

export class PerformanceMonitor {
  constructor() {
    this.metrics = {
      initialLoad: 0,
      chunkLoads: new Map(),
      navigationTimes: new Map(),
      preloadHits: 0,
      preloadMisses: 0
    };
    
    this.init();
  }
  
  init() {
    // Track initial page load performance
    if (typeof window !== 'undefined' && 'performance' in window) {
      window.addEventListener('load', () => {
        this.recordInitialLoad();
      });
      
      // Track resource loading
      this.observeResourceTiming();
    }
  }
  
  recordInitialLoad() {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation) {
      this.metrics.initialLoad = navigation.loadEventEnd - navigation.fetchStart;
      console.log(`📊 Initial Load Time: ${this.metrics.initialLoad.toFixed(2)}ms`);
    }
  }
  
  recordChunkLoad(chunkName, loadTime) {
    this.metrics.chunkLoads.set(chunkName, loadTime);
    console.log(`📦 Chunk "${chunkName}" loaded in ${loadTime.toFixed(2)}ms`);
  }
  
  recordNavigation(fromRoute, toRoute, duration) {
    const key = `${fromRoute} → ${toRoute}`;
    this.metrics.navigationTimes.set(key, duration);
    console.log(`🧭 Navigation ${key}: ${duration.toFixed(2)}ms`);
  }
  
  recordPreloadHit(chunkName) {
    this.metrics.preloadHits++;
    console.log(`🎯 Preload HIT for "${chunkName}"`);
  }
  
  recordPreloadMiss(chunkName) {
    this.metrics.preloadMisses++;
    console.log(`❌ Preload MISS for "${chunkName}"`);
  }
  
  observeResourceTiming() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name.includes('chunk') || entry.name.includes('.js')) {
            const fileName = entry.name.split('/').pop();
            const loadTime = entry.responseEnd - entry.fetchStart;
            
            if (fileName.includes('chunk')) {
              // Extract chunk name from filename
              const chunkMatch = fileName.match(/([a-zA-Z-]+)\.[\w.]+\.js/);
              if (chunkMatch) {
                this.recordChunkLoad(chunkMatch[1], loadTime);
              }
            }
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
    }
  }
  
  getReport() {
    const report = {
      initialLoadTime: this.metrics.initialLoad,
      totalChunksLoaded: this.metrics.chunkLoads.size,
      averageChunkLoadTime: this.getAverageChunkLoadTime(),
      preloadEfficiency: this.getPreloadEfficiency(),
      slowestChunks: this.getSlowestChunks(5),
      fastestNavigations: this.getFastestNavigations(5)
    };
    
    console.table({
      'Initial Load (ms)': report.initialLoadTime.toFixed(2),
      'Chunks Loaded': report.totalChunksLoaded,
      'Avg Chunk Time (ms)': report.averageChunkLoadTime.toFixed(2),
      'Preload Efficiency (%)': report.preloadEfficiency.toFixed(1),
    });
    
    return report;
  }
  
  getAverageChunkLoadTime() {
    if (this.metrics.chunkLoads.size === 0) return 0;
    const total = Array.from(this.metrics.chunkLoads.values()).reduce((sum, time) => sum + time, 0);
    return total / this.metrics.chunkLoads.size;
  }
  
  getPreloadEfficiency() {
    const total = this.metrics.preloadHits + this.metrics.preloadMisses;
    return total > 0 ? (this.metrics.preloadHits / total) * 100 : 100;
  }
  
  getSlowestChunks(count = 5) {
    return Array.from(this.metrics.chunkLoads.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, count);
  }
  
  getFastestNavigations(count = 5) {
    return Array.from(this.metrics.navigationTimes.entries())
      .sort((a, b) => a[1] - b[1])
      .slice(0, count);
  }
  
  // Export metrics to external analytics service
  exportMetrics() {
    return {
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      connection: navigator.connection?.effectiveType || 'unknown',
      metrics: {
        initialLoad: this.metrics.initialLoad,
        chunkLoads: Object.fromEntries(this.metrics.chunkLoads),
        preloadHits: this.metrics.preloadHits,
        preloadMisses: this.metrics.preloadMisses,
        navigationTimes: Object.fromEntries(this.metrics.navigationTimes)
      }
    };
  }
}

// Global instance
export const performanceMonitor = new PerformanceMonitor();

// Convenience methods
export const trackChunkLoad = (chunkName, loadTime) => {
  performanceMonitor.recordChunkLoad(chunkName, loadTime);
};

export const trackNavigation = (from, to, duration) => {
  performanceMonitor.recordNavigation(from, to, duration);
};

export const trackPreloadHit = (chunkName) => {
  performanceMonitor.recordPreloadHit(chunkName);
};

export const trackPreloadMiss = (chunkName) => {
  performanceMonitor.recordPreloadMiss(chunkName);
};

export const getPerformanceReport = () => {
  return performanceMonitor.getReport();
};